import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'dark' | 'light'
export type Density = 'compact' | 'cozy'

/**
 * Clave de localStorage. **El script inline de `index.html` lee exactamente
 * esta clave y esta forma** (`{ state: { theme, density } }`) para aplicar el
 * tema antes del primer paint. Si cambia una, cambian las dos.
 */
export const UI_STORAGE_KEY = 'pm-ui'

interface UiState {
  theme: Theme
  density: Density
  sidebarCollapsed: boolean
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  setDensity: (density: Density) => void
  toggleDensity: () => void
  toggleSidebar: () => void
}

function systemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: systemTheme(),
      density: 'compact',
      sidebarCollapsed: false,
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set({ theme: get().theme === 'dark' ? 'light' : 'dark' }),
      setDensity: (density) => set({ density }),
      toggleDensity: () => set({ density: get().density === 'compact' ? 'cozy' : 'compact' }),
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
    }),
    {
      name: UI_STORAGE_KEY,
      partialize: (state) => ({
        theme: state.theme,
        density: state.density,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    },
  ),
)

/** Escribe el estado en el `<html>`. Idempotente. */
export function applyUiPreferences(theme: Theme, density: Density): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.setAttribute('data-theme', theme)
  root.setAttribute('data-density', density)
  root.style.colorScheme = theme
}
